/**
 * M-Scale Bluetooth Service
 * Handles Web Bluetooth API communication with M-Scale device
 */

import {
  M_SCALE_SERVICE_UUID,
  CHARACTERISTIC_UUIDS,
  BLEError,
  BLEErrorCode,
  type ConnectionState,
  type HardwareConfig,
  type PairInfo,
  type TargetRange,
  type SystemStatus,
  type CalibrationStatus,
  type CalibrationData,
  type PositionMapEntry,
  SystemCommand,
  CalibrationCommand,
  LEDPattern,
  type LEDOverride,
  type DeviceLinkStatus,
  type DeviceLinkConfig,
  DeviceLinkCommand,
} from './types'

import {
  decodeAllWeights,
  decodePairData,
  decodeTargetRange,
  encodeTargetRange,
  decodeGroupSize,
  encodeGroupSize,
  decodeMinRatio,
  encodeMinRatio,
  decodeSystemStatus,
  encodeSystemCommand,
  encodeTareCommand,
  encodeCalibrationCommand,
  decodeCalibrationStatus,
  decodeCalibrationData,
  decodePositionMap,
  decodeHardwareConfig,
  dataViewToString,
  encodeLEDOverride,
  decodeDeviceLinkStatus,
  encodeDeviceLinkControl,
  decodeDeviceLinkConfig,
  encodeDeviceLinkConfig,
  decodeCustomSuffix,
  encodeCustomSuffix,
} from './protocol'

import { OTAService, type OTAStatus } from './OTAService'

export type WeightUpdateCallback = (weights: number[]) => void
export type GroupUpdateCallback = (pairs: PairInfo[], count: number) => void
export type StatusUpdateCallback = (status: SystemStatus) => void
export type CalibrationUpdateCallback = (status: CalibrationStatus) => void
export type OTAStatusCallback = (status: OTAStatus) => void
export type DisconnectCallback = () => void
export type DeviceLinkStatusCallback = (status: DeviceLinkStatus) => void

export class BluetoothService {
  private device: BluetoothDevice | null = null
  private server: BluetoothRemoteGATTServer | null = null
  private service: BluetoothRemoteGATTService | null = null
  private characteristics: Map<string, BluetoothRemoteGATTCharacteristic> = new Map()

  // OTA Service
  private otaService: OTAService = new OTAService()

  // Callbacks
  private weightCallback: WeightUpdateCallback | null = null
  private pairCallback: GroupUpdateCallback | null = null
  private statusCallback: StatusUpdateCallback | null = null
  private calibrationCallback: CalibrationUpdateCallback | null = null
  private disconnectCallback: DisconnectCallback | null = null
  private deviceLinkStatusCallback: DeviceLinkStatusCallback | null = null

  /**
   * Check if Web Bluetooth is supported
   */
  static isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'bluetooth' in navigator
  }

  /**
   * Connect to M-Scale device
   */
  async connect(): Promise<ConnectionState> {
    if (!BluetoothService.isSupported()) {
      throw new BLEError(
        'Web Bluetooth is not supported in this browser. Please use Chrome, Edge, or Opera',
        BLEErrorCode.NOT_SUPPORTED
      )
    }

    try {
      // Request device
      this.device = await navigator.bluetooth.requestDevice({
        filters: [
          { services: [M_SCALE_SERVICE_UUID] }
        ]
      })

      // Add disconnect listener
      this.device.addEventListener('gattserverdisconnected', this.handleDisconnect.bind(this))

      // Connect to GATT server
      if (!this.device.gatt) {
        throw new BLEError('Device does not support GATT', BLEErrorCode.CONNECTION_FAILED)
      }

      this.server = await this.device.gatt.connect()

      // Get primary service
      this.service = await this.server.getPrimaryService(M_SCALE_SERVICE_UUID)

      // Cache important characteristics
      await this.cacheCharacteristics()

      // Initialize OTA service
      try {
        await this.otaService.init(this.service)
        console.log('OTA service initialized')
      } catch (error) {
        console.warn('OTA service not available:', error)
        // OTA is optional, don't fail connection
      }

      // Small delay to let BLE stack settle after characteristic discovery
      await new Promise(resolve => setTimeout(resolve, 100))

      // Verify connection is still valid
      if (!this.server.connected) {
        throw new BLEError('Device disconnected during initialization', BLEErrorCode.CONNECTION_FAILED)
      }

      console.log('Connection established successfully')
      return {
        isConnected: true,
        deviceName: this.device.name || 'M-Scale',
        device: this.device,
        server: this.server
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'NotFoundError') {
          throw new BLEError('No device selected', BLEErrorCode.USER_CANCELLED, error)
        }
        if (error.name === 'NetworkError') {
          throw new BLEError('Connection failed. Make sure the device is powered on and nearby.', BLEErrorCode.CONNECTION_FAILED, error)
        }
      }
      throw new BLEError('Failed to connect to device', BLEErrorCode.UNKNOWN, error as Error)
    }
  }

  /**
   * Disconnect from device
   */
  async disconnect(): Promise<void> {
    if (this.server && this.server.connected) {
      this.server.disconnect()
    }
    this.cleanup()
  }

  /**
   * Get connection state
   */
  isConnected(): boolean {
    return this.server?.connected ?? false
  }

  /**
   * Get device name
   */
  getDeviceName(): string | null {
    return this.device?.name ?? null
  }

  /**
   * Cache frequently used characteristics
   */
  private async cacheCharacteristics(): Promise<void> {
    if (!this.service) return

    // Cache all implemented characteristics
    const charUUIDs = [
      CHARACTERISTIC_UUIDS.DEVICE_NAME,
      CHARACTERISTIC_UUIDS.FIRMWARE_VERSION,
      CHARACTERISTIC_UUIDS.HARDWARE_CONFIG,
      CHARACTERISTIC_UUIDS.ALL_WEIGHTS,
      CHARACTERISTIC_UUIDS.PAIR_COUNT,
      CHARACTERISTIC_UUIDS.PAIR_DATA,
      CHARACTERISTIC_UUIDS.TARGET_RANGE,
      CHARACTERISTIC_UUIDS.SYSTEM_STATUS,
      CHARACTERISTIC_UUIDS.TARE_COMMAND,
      CHARACTERISTIC_UUIDS.TIME_SYNC,
      CHARACTERISTIC_UUIDS.CALIBRATION_STATUS,
      CHARACTERISTIC_UUIDS.CALIBRATION_DATA,
      CHARACTERISTIC_UUIDS.DEVICE_LINK_STATUS,
      CHARACTERISTIC_UUIDS.DEVICE_LINK_CONTROL,
      CHARACTERISTIC_UUIDS.DEVICE_LINK_CONFIG,
    ]

    console.log(`Discovering ${charUUIDs.length} characteristics...`)
    let discoveredCount = 0
    for (const uuid of charUUIDs) {
      try {
        const char = await this.service.getCharacteristic(uuid)
        this.characteristics.set(uuid, char)
        discoveredCount++
      } catch (error) {
        console.warn(`Characteristic ${uuid} not available:`, error)
      }
    }
    console.log(`Discovered ${discoveredCount}/${charUUIDs.length} characteristics`)
  }

  /**
   * Get a characteristic (from cache or fetch)
   */
  private async getCharacteristic(uuid: string): Promise<BluetoothRemoteGATTCharacteristic> {
    if (this.characteristics.has(uuid)) {
      return this.characteristics.get(uuid)!
    }

    if (!this.service) {
      throw new BLEError('Not connected to service', BLEErrorCode.SERVICE_NOT_FOUND)
    }

    try {
      const char = await this.service.getCharacteristic(uuid)
      this.characteristics.set(uuid, char)
      return char
    } catch (error) {
      throw new BLEError(
        `Characteristic ${uuid} not found`,
        BLEErrorCode.CHARACTERISTIC_NOT_FOUND,
        error as Error
      )
    }
  }

  // ===== READ OPERATIONS =====

  /**
   * Read device information
   */
  async readDeviceInfo(): Promise<{ name: string; firmwareVersion: string; hardwareConfig: HardwareConfig }> {
    const [nameChar, versionChar, configChar] = await Promise.all([
      this.getCharacteristic(CHARACTERISTIC_UUIDS.DEVICE_NAME),
      this.getCharacteristic(CHARACTERISTIC_UUIDS.FIRMWARE_VERSION),
      this.getCharacteristic(CHARACTERISTIC_UUIDS.HARDWARE_CONFIG),
    ])

    const [nameData, versionData, configData] = await Promise.all([
      nameChar.readValue(),
      versionChar.readValue(),
      configChar.readValue(),
    ])

    return {
      name: dataViewToString(nameData),
      firmwareVersion: dataViewToString(versionData),
      hardwareConfig: decodeHardwareConfig(dataViewToString(configData)),
    }
  }

  /**
   * Read all current weights
   */
  async readWeights(): Promise<number[]> {
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.ALL_WEIGHTS)
    const data = await char.readValue()
    return decodeAllWeights(data)
  }

  /**
   * Read current pairs
   */
  async readGroups(): Promise<{ groups: PairInfo[]; count: number }> {
    const [countChar, dataChar] = await Promise.all([
      this.getCharacteristic(CHARACTERISTIC_UUIDS.PAIR_COUNT),
      this.getCharacteristic(CHARACTERISTIC_UUIDS.PAIR_DATA),
    ])

    const [countData, pairData] = await Promise.all([
      countChar.readValue(),
      dataChar.readValue(),
    ])

    return {
      count: countData.getUint8(0),
      groups: decodePairData(pairData),
    }
  }

  /**
   * Read target range
   */
  async readTargetRange(): Promise<TargetRange> {
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.TARGET_RANGE)
    const data = await char.readValue()
    return decodeTargetRange(data)
  }

  /**
   * Read group size
   */
  async readGroupSize(): Promise<number> {
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.GROUP_SIZE)
    const data = await char.readValue()
    return decodeGroupSize(data)
  }

  /**
   * Read system status
   */
  async readSystemStatus(): Promise<SystemStatus> {
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.SYSTEM_STATUS)
    const data = await char.readValue()
    return decodeSystemStatus(data)
  }

  /**
   * Read calibration data for a position
   */
  async readCalibrationData(_position: number): Promise<CalibrationData> {
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.CALIBRATION_DATA)
    const data = await char.readValue()
    return decodeCalibrationData(data)
  }

  /**
   * Read position map
   */
  async readPositionMap(): Promise<PositionMapEntry[]> {
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.POSITION_MAP)
    const data = await char.readValue()
    return decodePositionMap(data)
  }

  // ===== WRITE OPERATIONS =====

  /**
   * Write target range
   */
  async writeTargetRange(range: TargetRange): Promise<void> {
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.TARGET_RANGE)
    const data = encodeTargetRange(range)
    await char.writeValue(data)
  }

  /**
   * Write group size
   */
  async writeGroupSize(size: number): Promise<void> {
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.GROUP_SIZE)
    const data = encodeGroupSize(size)
    await char.writeValue(data)
  }

  /**
   * Read minimum ratio
   */
  async readMinRatio(): Promise<number> {
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.MIN_RATIO)
    const data = await char.readValue()
    return decodeMinRatio(data)
  }

  /**
   * Write minimum ratio
   */
  async writeMinRatio(ratio: number): Promise<void> {
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.MIN_RATIO)
    const data = encodeMinRatio(ratio)
    await char.writeValue(data)
  }

  /**
   * Read custom device name suffix
   */
  async readCustomSuffix(): Promise<string> {
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.CUSTOM_SUFFIX)
    const data = await char.readValue()
    return decodeCustomSuffix(data)
  }

  /**
   * Write custom device name suffix (4 hex characters)
   * Note: Device name will change after next restart
   */
  async writeCustomSuffix(suffix: string): Promise<void> {
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.CUSTOM_SUFFIX)
    const data = encodeCustomSuffix(suffix)
    await char.writeValue(data)
  }

  /**
   * Send tare command
   */
  async tare(position: number = 0xFF): Promise<void> {
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.TARE_COMMAND)
    const data = encodeTareCommand(position)
    await char.writeValue(data)
  }

  /**
   * Send system command
   */
  async sendSystemCommand(command: SystemCommand): Promise<void> {
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.SYSTEM_COMMAND)
    const data = encodeSystemCommand(command)
    await char.writeValue(data)
  }

  /**
   * Sync time with device for LED animation synchronization
   * Sends current Unix timestamp in milliseconds to align device clocks
   * Gracefully handles older firmware that doesn't support time sync
   */
  async syncTime(): Promise<void> {
    // Check if TIME_SYNC characteristic is available (requires firmware v1.2.7+)
    const char = this.characteristics.get(CHARACTERISTIC_UUIDS.TIME_SYNC)
    if (!char) {
      console.log('Time sync not available (firmware v1.2.7+ required)')
      return
    }

    const currentTimeMs = Date.now()

    // Send 64-bit timestamp
    const buffer = new ArrayBuffer(8)
    const view = new DataView(buffer)
    // JavaScript numbers are 64-bit floats, but we can store the integer timestamp
    // Split into two 32-bit integers for precision
    const high = Math.floor(currentTimeMs / 0x100000000)
    const low = currentTimeMs >>> 0
    view.setUint32(0, low, true)  // Little endian
    view.setUint32(4, high, true)

    await char.writeValue(buffer)
    console.log(`Time sync sent: ${currentTimeMs}ms (${new Date(currentTimeMs).toISOString()})`)
  }

  /**
   * Send calibration command
   */
  async sendCalibrationCommand(
    position: number,
    command: CalibrationCommand,
    knownWeight?: number
  ): Promise<void> {
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.CALIBRATION_COMMAND)
    const data = encodeCalibrationCommand(position, command, knownWeight)
    await char.writeValue(data)
  }

  /**
   * Send LED pattern command
   */
  async sendLEDPattern(pattern: LEDPattern): Promise<void> {
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.LED_PATTERN)
    const buffer = new ArrayBuffer(1)
    new DataView(buffer).setUint8(0, pattern)
    await char.writeValue(buffer)
  }

  /**
   * Send LED override command
   */
  async sendLEDOverride(override: LEDOverride): Promise<void> {
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.LED_OVERRIDE)
    const data = encodeLEDOverride(override)
    await char.writeValue(data)
  }

  // ===== NOTIFICATIONS =====

  /**
   * Subscribe to weight updates
   */
  async subscribeToWeights(callback: WeightUpdateCallback): Promise<void> {
    this.weightCallback = callback
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.ALL_WEIGHTS)

    char.addEventListener('characteristicvaluechanged', (event: Event) => {
      const target = event.target as BluetoothRemoteGATTCharacteristic
      const weights = decodeAllWeights(target.value!)
      this.weightCallback?.(weights)
    })

    await char.startNotifications()
  }

  /**
   * Subscribe to group updates
   */
  async subscribeToGroups(callback: GroupUpdateCallback): Promise<void> {
    this.pairCallback = callback

    const [countChar, dataChar] = await Promise.all([
      this.getCharacteristic(CHARACTERISTIC_UUIDS.PAIR_COUNT),
      this.getCharacteristic(CHARACTERISTIC_UUIDS.PAIR_DATA),
    ])

    let lastCount = 0
    let lastPairs: PairInfo[] = []

    countChar.addEventListener('characteristicvaluechanged', (event: Event) => {
      const target = event.target as BluetoothRemoteGATTCharacteristic
      lastCount = target.value!.getUint8(0)
      this.pairCallback?.(lastPairs, lastCount)
    })

    dataChar.addEventListener('characteristicvaluechanged', (event: Event) => {
      const target = event.target as BluetoothRemoteGATTCharacteristic
      lastPairs = decodePairData(target.value!)
      this.pairCallback?.(lastPairs, lastCount)
    })

    await Promise.all([
      countChar.startNotifications(),
      dataChar.startNotifications(),
    ])
  }

  /**
   * Subscribe to system status updates
   */
  async subscribeToStatus(callback: StatusUpdateCallback): Promise<void> {
    this.statusCallback = callback
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.SYSTEM_STATUS)

    char.addEventListener('characteristicvaluechanged', (event: Event) => {
      const target = event.target as BluetoothRemoteGATTCharacteristic
      const status = decodeSystemStatus(target.value!)
      this.statusCallback?.(status)
    })

    await char.startNotifications()
  }

  /**
   * Subscribe to calibration status updates
   */
  async subscribeToCalibration(callback: CalibrationUpdateCallback): Promise<void> {
    this.calibrationCallback = callback
    const char = await this.getCharacteristic(CHARACTERISTIC_UUIDS.CALIBRATION_STATUS)

    char.addEventListener('characteristicvaluechanged', (event: Event) => {
      const target = event.target as BluetoothRemoteGATTCharacteristic
      const status = decodeCalibrationStatus(target.value!)
      this.calibrationCallback?.(status)
    })

    await char.startNotifications()
  }

  /**
   * Subscribe to OTA status updates
   */
  async subscribeToOTAStatus(callback: OTAStatusCallback): Promise<void> {
    try {
      await this.otaService.subscribeToStatus(callback)
    } catch (error) {
      console.warn('Failed to subscribe to OTA status:', error)
      throw error
    }
  }

  /**
   * Upload firmware via OTA
   */
  async uploadFirmware(
    file: File,
    onProgress?: (percent: number, bytesTransferred: number, totalBytes: number) => void
  ): Promise<void> {
    try {
      await this.otaService.uploadFirmware(file, onProgress)
    } catch (error) {
      console.error('OTA upload failed:', error)
      throw error
    }
  }

  /**
   * Abort OTA update
   */
  async abortOTA(): Promise<void> {
    try {
      await this.otaService.abortUpdate()
    } catch (error) {
      console.error('Failed to abort OTA:', error)
      throw error
    }
  }

  /**
   * Set disconnect callback
   */
  onDisconnect(callback: DisconnectCallback): void {
    this.disconnectCallback = callback
  }

  // ===== PRIVATE METHODS =====

  // ===== Device Link (Dual-Device Mode) =====

  /**
   * Scan for available M-Scale devices
   * Returns list of devices that match M-Scale service
   */
  async scanForDevices(): Promise<BluetoothDevice[]> {
    if (!BluetoothService.isSupported()) {
      throw new BLEError('Web Bluetooth not supported', BLEErrorCode.NOT_SUPPORTED)
    }

    try {
      // Request device with acceptAllDevices to see all M-Scale devices
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: [M_SCALE_SERVICE_UUID] }],
        optionalServices: [M_SCALE_SERVICE_UUID]
      })

      // For now, return single device (Web Bluetooth doesn't support true scanning)
      // User will need to select from browser's device picker
      return device ? [device] : []
    } catch (error) {
      if (error instanceof Error && error.name === 'NotFoundError') {
        return [] // User cancelled
      }
      throw new BLEError('Failed to scan for devices', BLEErrorCode.UNKNOWN, error as Error)
    }
  }

  /**
   * Get device information (name and BLE address)
   * Note: Web Bluetooth API doesn't expose MAC address directly,
   * so we use device ID as a proxy
   */
  getDeviceInfo(device: BluetoothDevice): { name: string; id: string } {
    return {
      name: device.name || 'M-Scale',
      id: device.id, // Device ID (not MAC address, but unique identifier)
    }
  }

  /**
   * Get current device's BLE address bytes (for link control command)
   * Web Bluetooth doesn't expose MAC address, so we generate pseudo-address from device ID
   */
  private getDeviceAddressBytes(deviceId: string): number[] {
    // Generate 6-byte pseudo-address from device ID hash
    const bytes: number[] = []
    for (let i = 0; i < 6; i++) {
      const charCode = deviceId.charCodeAt(i % deviceId.length)
      bytes.push(charCode & 0xFF)
    }
    return bytes
  }

  /**
   * Send link control command to Primary device
   */
  async sendLinkCommand(command: DeviceLinkCommand, secondaryDeviceId?: string): Promise<void> {
    const char = this.characteristics.get(CHARACTERISTIC_UUIDS.DEVICE_LINK_CONTROL)
    if (!char) {
      throw new BLEError('Device Link Control characteristic not found', BLEErrorCode.CHARACTERISTIC_NOT_FOUND)
    }

    let encoded: Uint8Array
    if (command === DeviceLinkCommand.CONNECT_TO_SECONDARY && secondaryDeviceId) {
      const secondaryAddress = this.getDeviceAddressBytes(secondaryDeviceId)
      encoded = encodeDeviceLinkControl(command, secondaryAddress)
    } else {
      encoded = encodeDeviceLinkControl(command)
    }

    await char.writeValue(new Uint8Array(encoded))
    console.log(`Sent link command: ${command}`)
  }

  /**
   * Read device link status
   */
  async getDeviceLinkStatus(): Promise<DeviceLinkStatus> {
    const char = this.characteristics.get(CHARACTERISTIC_UUIDS.DEVICE_LINK_STATUS)
    if (!char) {
      throw new BLEError('Device Link Status characteristic not found', BLEErrorCode.CHARACTERISTIC_NOT_FOUND)
    }

    const value = await char.readValue()
    return decodeDeviceLinkStatus(value)
  }

  /**
   * Write device link configuration (layout)
   */
  async setDeviceLinkConfig(config: DeviceLinkConfig): Promise<void> {
    const char = this.characteristics.get(CHARACTERISTIC_UUIDS.DEVICE_LINK_CONFIG)
    if (!char) {
      throw new BLEError('Device Link Config characteristic not found', BLEErrorCode.CHARACTERISTIC_NOT_FOUND)
    }

    const encoded = encodeDeviceLinkConfig(config)
    await char.writeValue(new Uint8Array(encoded))
    console.log('Device link config updated:', config)
  }

  /**
   * Read device link configuration
   */
  async getDeviceLinkConfig(): Promise<DeviceLinkConfig> {
    const char = this.characteristics.get(CHARACTERISTIC_UUIDS.DEVICE_LINK_CONFIG)
    if (!char) {
      throw new BLEError('Device Link Config characteristic not found', BLEErrorCode.CHARACTERISTIC_NOT_FOUND)
    }

    const value = await char.readValue()
    return decodeDeviceLinkConfig(value)
  }

  /**
   * Read current device link status
   */
  async readDeviceLinkStatus(): Promise<DeviceLinkStatus> {
    const char = this.characteristics.get(CHARACTERISTIC_UUIDS.DEVICE_LINK_STATUS)
    if (!char) {
      throw new BLEError('Device Link Status characteristic not found', BLEErrorCode.CHARACTERISTIC_NOT_FOUND)
    }

    const value = await char.readValue()
    return decodeDeviceLinkStatus(value)
  }

  /**
   * Subscribe to device link status changes
   */
  async subscribeToDeviceLinkStatus(callback: DeviceLinkStatusCallback): Promise<void> {
    const char = this.characteristics.get(CHARACTERISTIC_UUIDS.DEVICE_LINK_STATUS)
    if (!char) {
      throw new BLEError('Device Link Status characteristic not found', BLEErrorCode.CHARACTERISTIC_NOT_FOUND)
    }

    this.deviceLinkStatusCallback = callback

    await char.startNotifications()
    char.addEventListener('characteristicvaluechanged', (event: Event) => {
      const target = event.target as BluetoothRemoteGATTCharacteristic
      const status = decodeDeviceLinkStatus(target.value!)
      this.deviceLinkStatusCallback?.(status)
    })

    console.log('Subscribed to device link status notifications')
  }

  /**
   * Unsubscribe from device link status
   */
  async unsubscribeFromDeviceLinkStatus(): Promise<void> {
    const char = this.characteristics.get(CHARACTERISTIC_UUIDS.DEVICE_LINK_STATUS)
    if (char) {
      await char.stopNotifications()
      this.deviceLinkStatusCallback = null
    }
  }

  private handleDisconnect(): void {
    console.log('Device disconnected')
    this.cleanup()
    this.disconnectCallback?.()
  }

  private cleanup(): void {
    this.characteristics.clear()
    this.service = null
    this.server = null
    this.weightCallback = null
    this.pairCallback = null
    this.statusCallback = null
    this.calibrationCallback = null
    this.deviceLinkStatusCallback = null
  }
}

// Singleton instance
export const bluetoothService = new BluetoothService()
